import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Log API configuration on init (helps with debugging)
console.log('API Configuration:', { BACKEND_URL, API });

// Store admin credentials for authenticated requests
let adminCredentials = null;

export const setAdminCredentials = (password) => {
  adminCredentials = password;
};

export const clearAdminCredentials = () => {
  adminCredentials = null;
};

// Helper to create auth headers
const getAuthHeaders = () => {
  if (adminCredentials) {
    // Use Basic Auth with empty username
    const encoded = btoa(`:${adminCredentials}`);
    return { Authorization: `Basic ${encoded}` };
  }
  return {};
};

// ============== Auth ==============

export const verifyAdminPassword = async (password) => {
  try {
    const response = await axios.post(`${API}/admin/verify`, { password });
    if (response.data.authenticated) {
      setAdminCredentials(password);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Auth error:', error);
    return false;
  }
};

// ============== Jobs ==============

export const getJobs = async (status = null) => {
  try {
    const url = `${API}/jobs`;
    const params = status ? { status } : {};
    console.log('Fetching jobs from:', url, 'with params:', params);
    const response = await axios.get(url, { params });
    console.log('Jobs response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching jobs:', error.message, error.response?.status, error.response?.data);
    throw error;
  }
};

export const getJob = async (jobId) => {
  try {
    const response = await axios.get(`${API}/jobs/${jobId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching job:', error);
    throw error;
  }
};

export const createJob = async (jobData) => {
  try {
    const response = await axios.post(`${API}/jobs`, jobData, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating job:', error);
    throw error;
  }
};

export const updateJob = async (jobId, jobData) => {
  try {
    const response = await axios.put(`${API}/jobs/${jobId}`, jobData, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating job:', error);
    throw error;
  }
};

export const deleteJob = async (jobId) => {
  try {
    const response = await axios.delete(`${API}/jobs/${jobId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting job:', error);
    throw error;
  }
};

// ============== Applications ==============

export const getApplications = async (jobId = null) => {
  try {
    const params = jobId && jobId !== 'all' ? { job_id: jobId } : {};
    const response = await axios.get(`${API}/applications`, {
      params,
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching applications:', error);
    throw error;
  }
};

export const createApplication = async (applicationData, resumeFile = null) => {
  try {
    // Use FormData to support file upload
    const formData = new FormData();
    formData.append('jobId', applicationData.jobId);
    formData.append('jobTitle', applicationData.jobTitle);
    formData.append('name', applicationData.name);
    formData.append('email', applicationData.email);
    formData.append('phone', applicationData.phone || 'Not provided');
    formData.append('coverLetter', applicationData.coverLetter || 'No cover letter provided');
    formData.append('linkedin', applicationData.linkedin || '');
    formData.append('portfolio', applicationData.portfolio || '');
    formData.append('github', applicationData.github || '');
    formData.append('twitter', applicationData.twitter || '');
    
    // Append the actual resume file if provided
    if (resumeFile) {
      formData.append('resume', resumeFile);
    }
    
    const response = await axios.post(`${API}/applications`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating application:', error);
    throw error;
  }
};

export const getResumeDownloadUrl = (appId) => {
  // Returns the URL for downloading a resume (requires auth)
  return `${API}/applications/${appId}/resume`;
};

export const downloadResume = async (appId, filename) => {
  try {
    const response = await axios.get(`${API}/applications/${appId}/resume`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || 'resume.pdf');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading resume:', error);
    throw error;
  }
};

export const updateApplicationStatus = async (appId, status) => {
  try {
    const response = await axios.put(`${API}/applications/${appId}/status`, { status }, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating application status:', error);
    throw error;
  }
};

// ============== Email Signups ==============

export const createEmailSignup = async (email) => {
  try {
    const response = await axios.post(`${API}/email-signup`, { email });
    return response.data;
  } catch (error) {
    console.error('Error creating email signup:', error);
    throw error;
  }
};

export const getEmailSignups = async () => {
  try {
    const response = await axios.get(`${API}/email-signups`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching email signups:', error);
    throw error;
  }
};
